/**
 * @fileoverview Data Layer (DL) for the FanMessages (Mensajes a la afición) dynamic.
 * Provides HTTP REST fetch requests to communicate with the FanMessages API controller endpoints.
 * 
 * @module utilities/DL/FanMessages
 */

/**
 * Retrieves the full list of active fan messages.
 * 
 * @async
 * @function GetMessages
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>} API response payload.
 */
export const GetMessages = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/GetMessages`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener la lista de mensajes a la afición.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al obtener los mensajes.' };
    }
};

/**
 * Retrieves a single fan message by its unique numeric identifier.
 * 
 * @async
 * @function GetMessage
 * @param {number} messageId - Unique identifier of the message.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetMessage = async (messageId) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/GetMessage/${messageId}`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al obtener el mensaje seleccionado.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al consultar el mensaje.' };
    }
};

/**
 * Creates or updates a fan message record.
 * Uses POST to 'SaveMessage' for new records (id == 0) or PUT to 'UpdateMessage/{id}' for existing ones.
 * 
 * @async
 * @function SaveMessage
 * @param {Object} messageData - Fan message payload object.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const SaveMessage = async (messageData) => {
    try {
        const isUpdate = messageData && messageData.id && Number(messageData.id) > 0;
        const endpointUrl = isUpdate
            ? `${process.env.REACT_APP_API_URL}FanMessages/UpdateMessage/${messageData.id}`
            : `${process.env.REACT_APP_API_URL}FanMessages/SaveMessage`;
        
        const httpMethod = isUpdate ? 'PUT' : 'POST';

        const fetchResponse = await fetch(endpointUrl, {
            method: httpMethod,
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(messageData)
        });

        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al guardar el mensaje a la afición.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al guardar el mensaje.' };
    }
};

/**
 * Logically deletes a fan message by its ID.
 * 
 * @async
 * @function DeleteMessage
 * @param {number} messageId - Unique identifier of the message to delete.
 * @returns {Promise<{success: boolean, message?: string}>} API response payload.
 */
export const DeleteMessage = async (messageId) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/DeleteMessage/${messageId}`, {
            method: 'DELETE'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al eliminar el mensaje a la afición.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al eliminar el mensaje.' };
    }
};

/**
 * Duplicates an existing fan message by ID.
 * 
 * @async
 * @function DuplicateMessage
 * @param {number} messageId - Unique identifier of the source message.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const DuplicateMessage = async (messageId) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/DuplicateMessage/${messageId}`, {
            method: 'POST'
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al duplicar el mensaje a la afición.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al duplicar el mensaje.' };
    }
};

/**
 * Selects a message as the active template in memory for stadium projection.
 * 
 * @async
 * @function SelectMessage
 * @param {number} messageId - Message identifier.
 * @param {string} [targetOverlay='all'] - Target overlay filter ('all', 'messages', 'selfiefan').
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const SelectMessage = async (messageId, targetOverlay = 'all') => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/SelectMessage`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id: Number(messageId),
                targetOverlay: targetOverlay || 'all'
            })
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al seleccionar el mensaje para proyección.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al seleccionar el mensaje.' };
    }
};

/**
 * Toggles the projection visibility of the message on stadium overlays.
 * 
 * @async
 * @function ToggleVisibility
 * @param {number|null} messageId - Message identifier to show, or null to hide active.
 * @param {boolean} isVisible - True to project, false to hide.
 * @param {string} [targetOverlay='all'] - Target overlay channel ('all', 'messages', 'selfiefan').
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const ToggleVisibility = async (messageId, isVisible, targetOverlay = 'all') => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/ToggleVisibility`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                id: messageId ? Number(messageId) : null,
                isVisible: Boolean(isVisible),
                targetOverlay: targetOverlay || 'all'
            })
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al alternar la visibilidad de proyección.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al alternar la visibilidad.' };
    }
};

/**
 * Retrieves the current in-memory global projection configuration.
 * 
 * @async
 * @function GetGlobalConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetGlobalConfig = async () => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/GetGlobalConfig`);
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al consultar la configuración global.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al consultar la configuración.' };
    }
};

/**
 * Updates the in-memory global projection configuration.
 * 
 * @async
 * @function UpdateGlobalConfig
 * @param {Object} configData - Global configuration payload.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const UpdateGlobalConfig = async (configData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/UpdateGlobalConfig`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(configData)
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al actualizar la configuración global.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al actualizar la configuración.' };
    }
};

/**
 * Uploads a single media file to be optimized with SkiaSharp on the backend.
 * 
 * @async
 * @function UploadMedia
 * @param {FormData} multipartFormData - Form data containing 'file' and optional 'type'.
 * @returns {Promise<{success: boolean, url?: string, message?: string}>} API response payload.
 */
export const UploadMedia = async (multipartFormData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/UploadMedia`, {
            method: 'POST',
            body: multipartFormData
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al subir el archivo multimedia.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al subir el archivo.' };
    }
};

/**
 * Uploads multiple media files in batch to be processed on the backend.
 * 
 * @async
 * @function UploadBatchMedia
 * @param {FormData} multipartFormData - Form data containing 'files' and optional 'type'.
 * @returns {Promise<{success: boolean, urls?: Array<string>, count?: number, message?: string}>} API response payload.
 */
export const UploadBatchMedia = async (multipartFormData) => {
    try {
        const fetchResponse = await fetch(`${process.env.REACT_APP_API_URL}FanMessages/UploadBatchMedia`, {
            method: 'POST',
            body: multipartFormData
        });
        if (!fetchResponse.ok) {
            const errorPayload = await fetchResponse.json().catch(() => ({}));
            return { success: false, message: errorPayload.message || 'Error al subir lote de archivos multimedia.' };
        }
        return await fetchResponse.json();
    } catch (networkError) {
        return { success: false, message: networkError.message || 'Error de conexión al subir el lote multimedia.' };
    }
};
